import classnames from 'classnames';
import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';
import { Image } from 'components/Image/Image';
import FreeTrialScoreDisplay from 'components/Score/FreeTrialScoreDisplay';
import LockedScoreDisplay from 'components/Score/LockedScoreDisplay';
import ScoreDisplay from 'components/Score/ScoreDisplay';
import ScorePotentialGuage from 'components/ScorePotentialGuage/ScorePotentialGuage';
import Label from 'components/form/Label/Label';
import { Link } from 'components/navigation/Link/Link';
import { isDateSameDateAsToday } from 'helpers/dateHelper';
import { getActionName } from 'partial/OfferManager/module.constants';
import React from 'react';
import { ReactComponent as ArrowRight } from 'style/_assets/icons/arrow-right.svg';
import { ReactComponent as EquifaxLogo } from 'style/_assets/logos/equifax_logo.svg';
import { ReactComponent as ExperianLogo } from 'style/_assets/logos/experian_logo.svg';
import { SESAME_POTENTIAL } from 'types/morph.pagePosition.constants';
import { ConditionalRendering } from '../../helpers/conditionalRedering.helper';
import './Score.scss';
import ConvertFreeTrialLink from './components/ConvertFreeTrialLink';
import UpdatedStatusLabel from './components/UpdatedStatusLabel';
import { OVERVIEW_URL } from 'types/mrph.routes.constants';
import { useSubscriptionLandingUrl } from 'hook/subscriptionLandingUrl.hook';

export const SesamePotentialLink = ({
  sesamePotentialConfig,
  onLinkClickCallback,
  sesamePotentialModuleName,
  modulePosition,
}) => {
  const actionName = sesamePotentialConfig
    ? sesamePotentialConfig.actionName
    : null;
  const actionLink = `/actions/${actionName}#sesamePotential`;
  return (
    <div id="sesamePotentialLink">
      <FlexContainer className="justify-center">
        <Link
          to={actionLink}
          onClickCallback={onLinkClickCallback}
          location={getActionName(actionName)}
          extraEventAttributes={{
            'Module Position': modulePosition,
            'Module Name': sesamePotentialModuleName,
          }}
        >
          <Label type="Subtitle-2" color="fontBlue100" className="link">
            Take Action
          </Label>
        </Link>
      </FlexContainer>
    </div>
  );
};

const SesamePotentialGraph = ({
  creditScore,
  datatestid,
  sesamePotentialConfig,
  onLinkClickCallback,
  sesamePotentialModuleName,
  modulePosition,
}) => {
  let sesamePotentialValue = sesamePotentialConfig.potentialIncrease;
  return (
    <FlexContainer className="flex-column autoCenter">
      <ScorePotentialGuage
        creditScore={creditScore}
        scoreChange={sesamePotentialValue}
        size={'medium'}
        datatestid={datatestid}
        sesamePotential={true}
        pagePosition={SESAME_POTENTIAL}
        sesamePotentialConfig={sesamePotentialConfig}
        onLinkClickCallback={onLinkClickCallback}
      />
      <div>
        <SesamePotentialLink
          onLinkClickCallback={onLinkClickCallback}
          sesamePotentialConfig={sesamePotentialConfig}
          sesamePotentialModuleName={sesamePotentialModuleName}
          modulePosition={modulePosition}
        />
      </div>
    </FlexContainer>
  );
};

export const CreditScoreRange = ({
  datatestid,
  showSesamePotential,
  isSmallDial,
}) => {
  const scoreRatingClass = classnames('scoreRange', 'flex-row', {
    sesamePotentialRange: showSesamePotential,
    isSmallDial: isSmallDial,
  });
  return (
    <FlexContainer
      className={scoreRatingClass}
      data-testid={datatestid ? `${datatestid}-scoreRange` : null}
    >
      <Label
        type="caption"
        color="fontGray300"
        datatestid={datatestid ? `${datatestid}-scoreRangeLow` : null}
        ariaHidden="true"
        tabIndex="-1"
      >
        300
      </Label>
      <Label
        type="caption"
        color="fontGray300"
        datatestid={datatestid ? `${datatestid}-scoreRangeHigh` : null}
        ariaHidden="true"
        tabIndex="-1"
      >
        850
      </Label>
    </FlexContainer>
  );
};

export const CreditScoreRangeActionLink = ({
  datatestid,
  showSesamePotential,
}) => {
  const scoreRatingClass = classnames('scoreRange', 'flex-row', {
    sesamePotentialRange: showSesamePotential,
  });
  return (
    <FlexContainer
      className={scoreRatingClass}
      data-testid={datatestid ? `${datatestid}-scoreRange` : null}
    >
      <Label
        type="caption"
        color="fontGray300"
        data-testid={datatestid ? `${datatestid}-scoreRangeLow` : null}
        ariaHidden="true"
        tabIndex="-1"
      >
        300
      </Label>
      <Label
        type="caption"
        color="fontGray300"
        data-testid={datatestid ? `${datatestid}-scoreRangeHigh` : null}
        ariaHidden="true"
        tabIndex="-1"
      >
        850
      </Label>
    </FlexContainer>
  );
};

const FreeTrialDashboardScoreView = ({
  datatestid,
  score,
  scoreChange,
  onClickDialPremium,
  onClickDialCreditFactor,
  onClickDialCreditScore,
  dashboardScoreClass,
  showSesamePotential,
  rangeExpNonPotential,
  isFreeTrial,
  isInvisibleUser,
  NextUpdateResult,
  show3BDialNonPremiumUser,
  isLastRefreshedToday,
  subscriptionLandingUrl,
}) => {
  let redirectLinkText = 'Convert your free trial to view all';

  let onClickItem = onClickDialCreditScore;

  if (show3BDialNonPremiumUser) {
    redirectLinkText = 'Sign up for Premium to get all three scores';

    onClickItem = onClickDialPremium;
  }
  return (
    <>
      <FlexContainer
        datatestid={datatestid}
        className="content-align-start isFreeTrialContainer mob-flex-column pos-rel padB40"
      >
        <div className={dashboardScoreClass}>
          <FreeTrialScoreDisplay
            score={score}
            scoreChange={scoreChange}
            datatestid={datatestid}
            showSesamePotential={showSesamePotential}
            isFreeTrial={isFreeTrial}
            onClickItem={onClickDialCreditFactor}
          />
          <div className={rangeExpNonPotential}>
            <CreditScoreRange
              datatestid={datatestid}
              showSesamePotential={showSesamePotential}
            />
          </div>
          <div className="padB16"></div>

          {!isInvisibleUser && (
            <div className="providerTu tuContainer">
              <Image imgClass="tuLogo bgImageH16" />
            </div>
          )}
          {!isInvisibleUser && !isLastRefreshedToday && (
            <FlexContainer className="mobileHide">
              <Label
                type="caption"
                color="fontGray300 text-8 vCenter text-center "
              >
                <NextUpdateResult />
              </Label>
            </FlexContainer>
          )}
        </div>
        <FlexContainer className="lockedScoreWrap m-margLRAuto m-margT20 ">
          <div className={`mobileHide ${dashboardScoreClass}`}>
            <LockedScoreDisplay
              datatestid={datatestid}
              onClickItem={onClickItem}
            />
            <div className={rangeExpNonPotential}>
              <CreditScoreRange datatestid={datatestid} />
            </div>
            <div className="padB18"></div>
            <div className="providerTu tuContainer">
              <EquifaxLogo />
            </div>
          </div>
          <div className={`mobileHide ${dashboardScoreClass}`}>
            <LockedScoreDisplay
              datatestid={datatestid}
              onClickItem={onClickItem}
            />
            <div className={rangeExpNonPotential}>
              <CreditScoreRange
                datatestid={datatestid}
                showSesamePotential={showSesamePotential}
              />
            </div>
            <div className="padB18"></div>
            <div className="providerTu tuContainer">
              <ExperianLogo />
            </div>
          </div>

          <ConvertFreeTrialLink
            show3BDialNonPremiumUser={show3BDialNonPremiumUser}
            onClickItem={onClickItem}
            redirectLinkText={redirectLinkText}
          />

          <div className={`desktopHide ${dashboardScoreClass}`}>
            <Link
              to={
                show3BDialNonPremiumUser
                  ? subscriptionLandingUrl
                  : '/creditReports/view'
              }
              className="m-padLR14"
              onClickCallback={onClickItem}
            >
              <LockedScoreDisplay
                datatestid={datatestid}
                onClickItem={onClickItem}
                mobileView
                isSmallDial
              />
            </Link>
            <div className={rangeExpNonPotential}>
              <CreditScoreRange datatestid={datatestid} isSmallDial={true} />
            </div>
            <div className="padB18"></div>
            <div className="providerTu tuContainer">
              <EquifaxLogo />
            </div>
          </div>
          <div className={`desktopHide ${dashboardScoreClass}`}>
            <Link
              to={
                show3BDialNonPremiumUser
                  ? subscriptionLandingUrl
                  : '/creditReports/view'
              }
              className="m-padLR14"
              onClickCallback={onClickItem}
            >
              <LockedScoreDisplay
                datatestid={datatestid}
                onClickItem={onClickItem}
                mobileView
                isSmallDial
              />
            </Link>

            <div className={rangeExpNonPotential}>
              <CreditScoreRange datatestid={datatestid} isSmallDial={true} />
            </div>
            <div className="padB18"></div>
            <div className="providerTu tuContainer">
              <ExperianLogo />
            </div>
          </div>
        </FlexContainer>
      </FlexContainer>
    </>
  );
};

const defaultCtaNull = {
  ratingData: {
    rating: <Label className="text-28 bold fontGray500">---</Label>,
  },
  title: 'Unable to Retrieve',
  subtitle: 'Check Back Later',
};

const DashboardScoreView = ({
  canPurchase3BReport,
  creditScore3BGating,
  dashboardScoreClass,
  datatestid,
  equifaxLastUpdateTime,
  experianLastUpdateTime,
  isFreeTrial,
  isInvisibleUser,
  isLastRefreshedToday,
  isPremiumUser,
  lastUpdateTime,
  modulePosition,
  NextUpdateResultFor3BGating,
  onClickItem,
  onLinkClickCallback,
  rangeExpNonPotential,
  score,
  scoreChange,
  sesamePotentialConfig,
  sesamePotentialModuleName,
  show3BDialToPremiumUser,
  showSesamePotential,
  tabletView,
  showNewWordDailyRefreshBanner,
}) => {
  const finalLink = isInvisibleUser ? OVERVIEW_URL : '/factors';
  const experianScore = creditScore3BGating?.filter(
    item => item?.bureau === 'Experian',
  )[0];
  const equifaxScore = creditScore3BGating?.filter(
    item => item?.bureau === 'Equifax',
  )[0];
  const showSesamePotentialScore =
    showSesamePotential && !(show3BDialToPremiumUser && creditScore3BGating);
  const containerClass = classnames(
    'content-align-start padB30 column-gap-30',
    {
      'mob-flex-row': showSesamePotentialScore,
      'mob-flex-column':
        !showSesamePotential &&
        (show3BDialToPremiumUser || creditScore3BGating),
    },
  );

  let redirectLinkText;

  if (canPurchase3BReport && show3BDialToPremiumUser) {
    redirectLinkText = 'Purchase your updated report';
  } else if (canPurchase3BReport) {
    redirectLinkText = 'Purchase your 3B report';
  }

  const equifaxScoreIsNull = !equifaxScore?.score;
  let equifaxDataNull = equifaxScoreIsNull ? defaultCtaNull : {};

  const experianScoreIsNull = !experianScore?.score;
  let experianDataNull = experianScoreIsNull ? defaultCtaNull : {};

  const BureausCTAPanelBelow = ({
    lastUpdateTime,
    ifBureausNull,
    bureauDataNull,
  }) => {
    if (ifBureausNull) {
      return (
        <>
          <FlexContainer className="justify-center">
            <span className="margT5">
              <Label type="caption" color="fontGray300">
                {bureauDataNull.title}
              </Label>
            </span>
          </FlexContainer>
          <FlexContainer className="mobileHide">
            <Label
              type="caption"
              color="fontGray300 text-8 vCenter text-center "
            >
              {bureauDataNull.subtitle}
            </Label>
          </FlexContainer>
        </>
      );
    }
    return (
      <>
        {(!isLastRefreshedToday || show3BDialToPremiumUser) && (
          <UpdatedStatusLabel lastUpdateTime={lastUpdateTime} />
        )}

        <FlexContainer className="mobileHide">
          <Label type="caption" color="fontGray300 text-8 vCenter text-center ">
            <NextUpdateResultFor3BGating />
          </Label>
        </FlexContainer>
      </>
    );
  };

  return (
    <>
      <FlexContainer datatestid={datatestid} className={containerClass}>
        <div className={dashboardScoreClass}>
          <Link to={finalLink} className="factorpagelink-container">
            <ScoreDisplay
              score={score}
              scoreChange={scoreChange}
              datatestid={datatestid}
              onClickItem={onClickItem}
              showSesamePotential={showSesamePotential}
              show3BDialToPremiumUser={show3BDialToPremiumUser}
              tabletView={tabletView}
            />
          </Link>

          <div className={rangeExpNonPotential}>
            <CreditScoreRange
              datatestid={datatestid}
              showSesamePotential={showSesamePotential}
            />
          </div>
          <div className="padB16"></div>

          <div className="providerTu tuContainer">
            <Image imgClass="tuLogo bgImageH16" />
          </div>
          {!isInvisibleUser &&
            !showNewWordDailyRefreshBanner &&
            (isFreeTrial || isPremiumUser) &&
            (showSesamePotential || show3BDialToPremiumUser) && (
              <UpdatedStatusLabel
                lastUpdateTime={
                  show3BDialToPremiumUser || creditScore3BGating
                    ? 'Daily'
                    : lastUpdateTime
                }
              />
            )}
        </div>
        {show3BDialToPremiumUser && creditScore3BGating && (
          <FlexContainer className="lockedScoreWrap m-margLRAuto m-margT20 column-gap-30">
            <div className={`mobileHide ${dashboardScoreClass}`}>
              <Link
                to="/creditReports/view"
                className="factorpagelink-container"
              >
                <ScoreDisplay
                  score={equifaxScore?.score}
                  datatestid={datatestid}
                  onClickItem={onClickItem}
                  showSesamePotential={showSesamePotential}
                  tabletView={tabletView}
                  ratingData={equifaxDataNull?.ratingData}
                />
                <div className={rangeExpNonPotential}>
                  <CreditScoreRange datatestid={datatestid} />
                </div>
                <div className="padB18"></div>
                <div className="providerTu tuContainer">
                  <EquifaxLogo />
                </div>
                <BureausCTAPanelBelow
                  lastUpdateTime={equifaxLastUpdateTime}
                  ifBureausNull={equifaxScoreIsNull}
                  bureauDataNull={equifaxDataNull}
                />
              </Link>
            </div>
            <div className={`mobileHide ${dashboardScoreClass}`}>
              <Link
                to="/creditReports/view"
                className="factorpagelink-container"
              >
                <ScoreDisplay
                  score={experianScore?.score}
                  datatestid={datatestid}
                  onClickItem={onClickItem}
                  showSesamePotential={showSesamePotential}
                  tabletView={tabletView}
                  ratingData={experianDataNull?.ratingData}
                />
                <div className={rangeExpNonPotential}>
                  <CreditScoreRange
                    datatestid={datatestid}
                    showSesamePotential={showSesamePotential}
                  />
                </div>
                <div className="padB18"></div>
                <div className="providerTu tuContainer">
                  <ExperianLogo />
                </div>
                <BureausCTAPanelBelow
                  lastUpdateTime={experianLastUpdateTime}
                  ifBureausNull={experianScoreIsNull}
                  bureauDataNull={experianDataNull}
                />
              </Link>
            </div>
            <div className={`desktopHide ${dashboardScoreClass} m-margR8`}>
              <Link
                to="/creditReports/view"
                className="factorpagelink-container"
              >
                <ScoreDisplay
                  score={equifaxScore?.score}
                  datatestid={datatestid}
                  onClickItem={onClickItem}
                  mobileView
                  isSmallDial
                  ratingData={equifaxDataNull?.ratingData}
                />
                <div className={rangeExpNonPotential}>
                  <CreditScoreRange
                    datatestid={datatestid}
                    isSmallDial={true}
                  />
                </div>
                <div className="padB18"></div>
                <div className="providerTu tuContainer">
                  <EquifaxLogo />
                </div>
                <BureausCTAPanelBelow
                  lastUpdateTime={equifaxLastUpdateTime}
                  ifBureausNull={equifaxScoreIsNull}
                  bureauDataNull={equifaxDataNull}
                />
              </Link>
            </div>
            <div className={`desktopHide ${dashboardScoreClass} m-margL8`}>
              <Link
                to="/creditReports/view"
                className="factorpagelink-container"
              >
                <ScoreDisplay
                  score={experianScore?.score}
                  datatestid={datatestid}
                  onClickItem={onClickItem}
                  mobileView
                  isSmallDial
                  ratingData={experianDataNull?.ratingData}
                />
                <div className={rangeExpNonPotential}>
                  <CreditScoreRange
                    datatestid={datatestid}
                    isSmallDial={true}
                  />
                </div>
                <div className="padB18"></div>
                <div className="providerTu tuContainer">
                  <ExperianLogo />
                </div>
                <BureausCTAPanelBelow
                  lastUpdateTime={experianLastUpdateTime}
                  ifBureausNull={experianScoreIsNull}
                  bureauDataNull={experianDataNull}
                />
              </Link>
            </div>
          </FlexContainer>
        )}
        {isPremiumUser && canPurchase3BReport && (
          <FlexContainer
            style={{ bottom: 0 }}
            className="convertfreetrial-link pos-abs text-12 m-format100 format60 justify-center m-text-center"
          >
            <Link to="/creditReports/view">
              {redirectLinkText} <ArrowRight />
            </Link>
          </FlexContainer>
        )}

        {showSesamePotentialScore && (
          <SesamePotentialGraph
            creditScore={score}
            sesamePotentialConfig={sesamePotentialConfig}
            onLinkClickCallback={onLinkClickCallback}
            sesamePotentialModuleName={sesamePotentialModuleName}
            modulePosition={modulePosition}
          />
        )}
      </FlexContainer>

      <ConditionalRendering
        isTrue={
          showNewWordDailyRefreshBanner ||
          (!showNewWordDailyRefreshBanner && !isPremiumUser && !isFreeTrial)
        }
      >
        <div
          className={classnames('text-12 bold margb16 text-center margTopN10', {
            margb40: isPremiumUser && canPurchase3BReport,
          })}
        >
          <ConditionalRendering isTrue={showNewWordDailyRefreshBanner}>
            <span className="bgYellow100 padlr12  padtb4 borderRad16 margr4">
              New
            </span>
          </ConditionalRendering>
          Daily score updates from TransUnion
        </div>
      </ConditionalRendering>
    </>
  );
};

function DashboardScore({
  canPurchase3BReport,
  tabletView,
  datatestid,
  score,
  scoreChange,
  onClickDialPremium,
  onClickDialCreditFactor,
  sesamePotentialConfig,
  onLinkClickCallback,
  onClickDialCreditScore,
  lastUpdateTime,
  isInvisibleUser,
  show3BDialNonPremiumUser,
  isFreeTrial,
  NextUpdateResult,
  sesamePotentialModuleName,
  modulePosition,
  show3BDialToPremiumUser,
  creditScore3BGating,
  NextUpdateResultFor3BGating,
  equifaxLastUpdateTime,
  experianLastUpdateTime,
  creditInfo,
  isPremiumUser,
  showNewWordDailyRefreshBanner,
}) {
  const showSesamePotential =
    sesamePotentialConfig?.potentialEligible &&
    sesamePotentialConfig?.potentialIncrease &&
    !creditScore3BGating;

  const dashboardScoreClass = classnames('dashboardScore', 'autoCenter', {
    sesamePotentialScoreWidth: showSesamePotential || tabletView,
    smallDial:
      !showSesamePotential &&
      !isFreeTrial &&
      !show3BDialNonPremiumUser &&
      !show3BDialToPremiumUser,
    isFreeTrialWidth: isFreeTrial,
    'm-margRAuto':
      isFreeTrial || (show3BDialToPremiumUser && creditScore3BGating),
  });
  const rangeExpNonPotential = classnames({
    rangeWidth: !showSesamePotential,
  });

  const isLastRefreshedToday = isDateSameDateAsToday(
    creditInfo?.lastRefreshDate,
  );

  const { subscriptionLandingUrl } = useSubscriptionLandingUrl();

  const isNonFreeTrialPremiumUser =
    !isFreeTrial &&
    (!show3BDialNonPremiumUser ||
      (show3BDialToPremiumUser && creditScore3BGating));

  return (
    <>
      {isNonFreeTrialPremiumUser ? (
        <DashboardScoreView
          canPurchase3BReport={canPurchase3BReport}
          datatestid={datatestid}
          dashboardScoreClass={dashboardScoreClass}
          tabletView={tabletView}
          score={score}
          scoreChange={scoreChange}
          onClickItem={onClickDialCreditFactor}
          showSesamePotential={showSesamePotential}
          rangeExpNonPotential={rangeExpNonPotential}
          lastUpdateTime={lastUpdateTime}
          isInvisibleUser={isInvisibleUser}
          sesamePotentialConfig={sesamePotentialConfig}
          onLinkClickCallback={onLinkClickCallback}
          sesamePotentialModuleName={sesamePotentialModuleName}
          modulePosition={modulePosition}
          show3BDialToPremiumUser={show3BDialToPremiumUser}
          creditScore3BGating={creditScore3BGating}
          NextUpdateResultFor3BGating={NextUpdateResultFor3BGating}
          equifaxLastUpdateTime={equifaxLastUpdateTime}
          experianLastUpdateTime={experianLastUpdateTime}
          isLastRefreshedToday={isLastRefreshedToday}
          isFreeTrial={isFreeTrial}
          isPremiumUser={isPremiumUser}
          showNewWordDailyRefreshBanner={showNewWordDailyRefreshBanner}
        />
      ) : (
        <FreeTrialDashboardScoreView
          canPurchase3BReport={canPurchase3BReport}
          datatestid={datatestid}
          dashboardScoreClass={dashboardScoreClass}
          isFreeTrial={isFreeTrial}
          showSesamePotential={showSesamePotential}
          rangeExpNonPotential={rangeExpNonPotential}
          lastUpdateTime={lastUpdateTime}
          NextUpdateResult={NextUpdateResult}
          score={score}
          scoreChange={scoreChange}
          onClickDialPremium={onClickDialPremium}
          onClickDialCreditFactor={onClickDialCreditFactor}
          onClickDialCreditScore={onClickDialCreditScore}
          show3BDialNonPremiumUser={show3BDialNonPremiumUser}
          equifaxLastUpdateTime={equifaxLastUpdateTime}
          experianLastUpdateTime={experianLastUpdateTime}
          isLastRefreshedToday={isLastRefreshedToday}
          isPremiumUser={isPremiumUser}
          subscriptionLandingUrl={subscriptionLandingUrl}
        />
      )}
    </>
  );
}

export { DashboardScore };
